import Card from "@rio-cloud/rio-uikit/Card";
import { Button } from "@rio-cloud/rio-uikit";
import { useT } from "@hooks";
import { formatValue, parseCurrencyBRL } from "src/utils/format";
import { RevenueCategory } from "src/types/dashboard";

interface CategoryCardProps {
  title: string;
  categories: RevenueCategory[];
  addButtonText: string;
  onViewClick?: () => void;
  onAddClick?: () => void;
}

export default function CategoryCard({
  title,
  categories,
  addButtonText,
  onViewClick,
  onAddClick,
}: Readonly<CategoryCardProps>) {
  const t = useT();
  const normalizeValue = (value: RevenueCategory["value"]) =>
    typeof value === "number" ? value : parseCurrencyBRL(String(value));

  const topCategories = [...categories]
    .map((category) => ({ ...category, value: normalizeValue(category.value) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  const maxValue = topCategories.length ? Math.max(...topCategories.map((c) => c.value)) : 0;

  const emptyBarsCount = Math.max(0, 5 - topCategories.length);
  const emptyBars = Array.from({ length: emptyBarsCount }, (_, i) => ({
    name: `empty_${i}`,
    value: 0,
    color: undefined as string | undefined,
    isEmpty: true,
  }));

  const allItems = [...topCategories, ...emptyBars];

  return (
    <Card className="padding-20 padding-x-15 padding-x-50-md">
      <span className="text-size-16 text-bold">{title}</span>

      <div className="display-flex justify-content-between gap-15 margin-y-20">
        <div className="width-100pct" style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          {allItems.map((item) => {
            const widthPercent = maxValue === 0 || item.value === 0 ? 0 : (item.value / maxValue) * 100;
            const barColor = item.color || "bg-coldplay-moos";
            return (
              <div key={item.name} className="position-relative width-100pct rounded-small bg-lightest">
                <div
                  className={`display-flex align-items-center rounded-small ${barColor}`}
                  style={{ height: "30px", width: `${widthPercent}%`, opacity: 0.75 }}
                />
                <div className="position-absolute left-0 top-50pct translate-y-50pct display-flex width-100pct padding-left-10">
                  <div className={"isEmpty" in item ? "" : "text-medium text-color-black"}>
                    {"isEmpty" in item ? "" : item.name}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          {allItems.map((item) => (
            <div
              key={item.name}
              className="display-flex align-items-center justify-content-end"
              style={{ height: "30px" }}
            >
              <div>
                {item.value > 0 ? (
                  <span className="text-align-right text-nowrap text-medium">{formatValue(item.value)}</span>
                ) : (
                  <span />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="display-flex align-items-center gap-10">
        <Button bsStyle="primary" className="action-button" onClick={onAddClick}>
          <span className="rioglyph rioglyph-plus-light" aria-hidden="true" />
          <span>{addButtonText}</span>
        </Button>
        <Button className="action-button" onClick={onViewClick}>
          <span className="rioglyph rioglyph-search-list" aria-hidden="true" />
          <span>{t("dashboard.button.viewAll")}</span>
        </Button>
      </div>
    </Card>
  );
}
